<template>
  <div class="file-wrapper">
    <!-- slot for parent component to activate the file changer -->
    <div @click="launchFilePicker()" id="uploadArea" ref="uploadArea" v-if="dragAndDropCapable">
      <slot name="activator"></slot>
    </div>
    <input type="file" ref="file" :name="uploadFieldName" @change="onFileChange($event.target.name, $event.target.files)" style="display: none" />

    <div class="file-content">
      <span><strong>Image Name</strong></span>
      <b-input id="imageName" v-model="imageName" :disabled="isDisabled" />
      <span><strong>Image Description</strong> (for screen readers)</span>
      <b-form-textarea id="imageDescription" v-model="imageDescription" rows="4" max-rows="4" :disabled="isDisabled" />
      <a class="remove" @click="removeExistingFile($event)">Delete Image</a>
    </div>

    <b-form-invalid-feedback aria-live="polite" class="d-block red" v-if="error"
      ><font-awesome-icon icon="exclamation-circle" size="1x" style="color: red" class="mr-1 fas fa-exclamation-circle" />{{
        errorText
      }}</b-form-invalid-feedback
    >
  </div>
</template>

<script>
import axios from 'axios';

// Refrain from continued use of this plugin, we are trying to move away from use the photos collection in the DB - use FileInput instead

export default {
  name: 'ImageUpload',
  data() {
    return {
      dragAndDropCapable: true,
      error: false,
      errorText: 'Something went wrong',
      uploadFieldName: 'file',
      maxSize: 1024,
      imageName: '',
      imageDescription: '',
      imageExtension: null,
      imageFile: null,
      disableFields: true,
    };
  },
  props: {
    value: Object,
    fileName: {
      type: String,
    },
    id: {
      type: Number,
      required: false,
    },
    context: {
      type: String,
    },
    imageNameInit: {
      type: String,
    },
    imageDescriptionInit: {
      type: String,
    },
  },
  computed: {
    isDisabled() {
      return this.disableFields;
    },
  },
  mounted() {
    this.$parent.$on('roadtrips-loaded', this.roadtripsLoaded);
    const div = document.createElement('div');
    this.dragAndDropCapable = ('draggable' in div || ('ondragstart' in div && 'ondrop' in div)) && 'FormData' in window && 'FileReader' in window;
    if (this.dragAndDropCapable) {
      ['drag', 'dragstart', 'dragend', 'dragover', 'dragenter', 'dragleave', 'drop'].forEach((evt) => {
        if (this.$refs.uploadArea) {
          this.$refs.uploadArea.addEventListener(
            evt,
            (e) => {
              e.preventDefault();
              e.stopPropagation();
            },
            false
          );
        }
      });
      if (this.$refs.uploadArea) {
        this.$refs.uploadArea.addEventListener('drop', (e) => {
          const file = e.dataTransfer.files;
          this.onFileChange('file', file);
        });
      }
    }
  },
  watch: {
    imageName(newValue) {
      const fileExtensionPattern = /\.([0-9a-z]+)(?=[?#])|(\.)(?:[\w]+)$/gim;
      let { imageExtension } = this;
      let imageName;

      if (this.imageName) {
        imageName = this.imageName.substr(0, this.imageName.lastIndexOf('.')) || this.imageName;
      }
      if (!imageExtension) {
        // repair image extension if one does not exist
        this.imageExtension = this.imageNameInit.match(fileExtensionPattern);
        imageExtension = this.imageNameInit.match(fileExtensionPattern);
      }

      const isValidFilename = this.isValidFilename(imageName + imageExtension);

      if (isValidFilename && newValue) {
        this.$emit('child-imageName', { imageExtension, imageName });
        this.$emit('filenameError', false);
      } else {
        this.$emit('filenameError', true);
      }
    },
    imageDescription(newValue) {
      if (newValue !== '') {
        this.$emit('child-imageDescription', this.imageDescription);
      }
    },
  },
  methods: {
    isValidFilename(fname) {
      // eslint-disable-next-line
      const rg1 = /^[^\\/:\*\?"<>\|]+$/; // forbidden characters \ / : * ? " < > |
      const rg2 = /^\./; // cannot start with dot (.)
      const rg3 = /\.(jpe?g|png|gif)$/i; // only jpg/jpeg,png, or gif allowed
      const rg4 = /^[^.\n]*\.[^.\n]*$/; // only one period allowed
      const rg5 = /\s/; // no spaces
      return rg1.test(fname) && !rg2.test(fname) && rg3.test(fname) && rg4.test(fname) && !rg5.test(fname);
    },
    removeExistingFile() {
      this.$emit('child-imageContext', this.context);
      this.imageName = '';
      this.imageDescription = '';
    },
    roadtripsLoaded() {
      if (this.imageNameInit) {
        this.imageName = this.imageNameInit;
      }
      this.imageDescription = this.imageDescriptionInit;
      if (this.imageName) {
        this.disableFields = false;
      }
    },
    launchFilePicker() {
      this.$refs.file.click();
    },
    onFileChange(fieldName, file) {
      this.disableFields = false;
      const imageContext = this.context;
      const { maxSize } = this;
      const imageFile = file[0];
      this.imageFile = imageFile;

      // get only file name, not extension and store it.
      this.imageName = imageFile.name.substr(0, imageFile.name.lastIndexOf('.')) || imageFile.name;
      const fileExtensionPattern = /\.([0-9a-z]+)(?=[?#])|(\.)(?:[\w]+)$/gim;
      this.imageExtension = imageFile.name.match(fileExtensionPattern);

      if (file.length > 0) {
        const size = imageFile.size / maxSize / maxSize;
        if (!imageFile.type.match('image.*')) {
          // check whether the upload is an image
          this.error = true;
          this.errorText = 'Please choose an image file';
        } else if (size > 1) {
          // check whether the size is greater than the size limit
          this.error = true;
          this.errorText = 'Image size exceeded. Please select an image under 1MB';
          this.$emit('fileSizeError', this.error);
        } else {
          // Append file into FormData and turn file into image URL
          const formData = new FormData();
          const imageURL = URL.createObjectURL(imageFile);
          formData.append(fieldName, imageFile);
          // Emit the FormData and image URL to the parent component
          this.$emit('input', { formData, imageURL, imageContext });
        }
      }
    },
    async uploadFile(file) {
      if (file.oldName !== file.newName || file.newFile) {
        let fileObj;
        if (file.newFile) {
          fileObj = {
            image_name: file.name,
            type: file.type,
            area: file.area,
            id: file.id,
            new_file: file.newFile,
          };
        } else {
          fileObj = {
            image_name: file.newName,
            old_image_name: file.oldName,
            area: file.area,
            id: file.id,
            new_file: file.newFile,
          };
        }

        let response = null;
        let thumbnailUrlResponse = null;

        response = await axios.post(`${process.env.VUE_APP_ROADTRIPS}/getUrlForUpload`, JSON.stringify(fileObj));
        if (file.area === 'featured_image') {
          // Id on new thumbnail going in is null, we need to get the id returned from 'response'
          const modifiedFileObj = fileObj;
          modifiedFileObj.id = response.data.roadtripId;
          thumbnailUrlResponse = await axios.post(`${process.env.VUE_APP_ROADTRIPS}/thumbnailUploadUrl`, JSON.stringify(modifiedFileObj));
        }
        if (file.newFile) {
          try {
            // eslint-disable-next-line
            delete axios.defaults.headers.common['Authorization'];

            const options = {
              headers: {
                'Content-Type': file.type,
                'Access-Control-Allow-Origin': '*',
              },
            };

            await axios.put(response.data.url, file, options);

            // if a featured_image, we need to upload image to thumbnail_image bucket as well.
            if (file.area === 'featured_image') {
              await axios.put(thumbnailUrlResponse.data.url, file, options);
            }

            // have to add back the authorization header
            axios.defaults.headers.common.Authorization = this.$store.state.auth.user.tokens.IdToken;
          } catch (err) {
            // eslint-disable-next-line no-console
            console.log(err);
          }
        }
        return response.data.roadtripId;
      }
      return file.id;
    },
  },
};
</script>

<style scoped>
.file-wrapper {
  /* border: #4e97b2 solid 1px; */
}

.file-content {
  margin-top: 25px;
}
</style>
